//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  BulkUploadErrorSource,
  BulkUploadProductError,
} from '@campaigns/types/sampling/samplingCampaignBulkUploadTypes';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { saveBlob } from '@utils/saveBlob';

const DELIMITER_PRODUCTS_REGEX = /\[(.*?)\](.*?)(?=\[|$)/g;
const PRODUCT_ID_REGEX = /\[(.*?)\]/;
const DELIMITER_INDIVIDUAL_ERROR = ';';

export const parseBackendErrors = (
  requestError: string,
): BulkUploadProductError[] => {
  const productErrorMatches = requestError.match(DELIMITER_PRODUCTS_REGEX);

  if (!productErrorMatches?.length) {
    return [
      {
        source: BulkUploadErrorSource.BACKEND,
        errors: [
          'Failed to upload products. Please check your file format and try again.',
        ],
      },
    ];
  }

  const structuredErrors: BulkUploadProductError[] = productErrorMatches.map(
    (errorStr) => {
      const productIdMatch = PRODUCT_ID_REGEX.exec(errorStr);
      if (!productIdMatch?.[1]) {
        return {
          source: BulkUploadErrorSource.BACKEND,
          errors: ['There was an error processing the file'],
        };
      }

      const productId = productIdMatch[1];
      const errorsString = errorStr.replace(PRODUCT_ID_REGEX, '').trim();
      const errors = errorsString
        .split(DELIMITER_INDIVIDUAL_ERROR)
        .map((err) => err.trim())
        .filter(Boolean);

      return {
        source: BulkUploadErrorSource.PRODUCT,
        productId,
        errors,
      };
    },
  );

  return structuredErrors;
};

const getSectionHeader = (error: BulkUploadProductError): string => {
  switch (error.source) {
    case BulkUploadErrorSource.PRODUCT:
      return `Product ${error.productId}`;
    case BulkUploadErrorSource.BACKEND:
      return 'Backend Error';
    default:
      return 'File Validation Error';
  }
};

/**
 * Generates formatted error text content for a downloadable error report file.
 * This is specifically for creating a text file export, not for displaying errors in the UI.
 */
export const generateErrorText = (
  errors: BulkUploadProductError[],
  timeZone: TimeZoneId,
  fileName?: string,
): string => {
  const dateString = DateString.fromTimestamp(Date.now(), timeZone);
  const currentDate = DateString.format(dateString, 'MMM D, YYYY');

  const header = [
    'Product import error report',
    `File: ${fileName ?? 'Unknown file'}`,
    `Date: ${currentDate}`,
    '',
  ].join('\n');

  const errorContent = errors
    .map((error) => {
      const sectionHeader = getSectionHeader(error);

      // Format each error as a bullet point in the list
      const errorList = error.errors.map((err) => `• ${err}`).join('\n');

      return `${sectionHeader}\n${errorList}`;
    })
    .join('\n\n');

  return `${header}\n${errorContent}`;
};

export const downloadErrorReport = (
  errors: BulkUploadProductError[],
  timeZone: TimeZoneId,
  fileName?: string,
) => {
  const errorContent = generateErrorText(errors, timeZone, fileName);
  saveBlob(errorContent, 'import-errors.txt', { 'content-type': 'text/plain' });
};
